import React, { useEffect } from 'react';
import {
    Editor,
    EditorState,
    RichUtils,
    DraftEditorCommand,
    convertToRaw,
    convertFromRaw,
    ContentState,
} from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import htmlToDraft from 'html-to-draftjs';
import 'draft-js/dist/Draft.css';
import { linkDecorator } from './Link';
import { mediaBlockRenderer } from './Media';
import './styles.scss';
export interface TextEditorInterface {
    value?: string;
    getText?: (text: string) => void;
    setEventId?: string;
    languageValue: string;
}

const TextEditor: React.FC<TextEditorInterface> = (props: TextEditorInterface) => {
    const getStateData = (value?: string) => {
        const blocksFromHtml = value ? htmlToDraft(value) : htmlToDraft('');
        const { contentBlocks, entityMap } = blocksFromHtml;
        const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
        const editorStateHTML = EditorState.createWithContent(contentState);
        return JSON.stringify(convertToRaw(editorStateHTML.getCurrentContent()));
    };

    const data = getStateData(props.value);
    const initialState = data
        ? EditorState.createWithContent(convertFromRaw(JSON.parse(data)), linkDecorator)
        : EditorState.createEmpty(linkDecorator);

    const [editorState, setEditorState] = React.useState<EditorState>(initialState);

    useEffect(() => {
        const data = getStateData(props.value);
        if (props.value != null) {
            setEditorState(EditorState.createWithContent(convertFromRaw(JSON.parse(data)), linkDecorator));
        }
    }, [props.setEventId, props.languageValue]);

    const handleSave = () => {
        const content = stateToHTML(editorState.getCurrentContent());
        if (!props.getText) return;
        props.getText(content);
    };
    useEffect(() => {
        handleSave();
    }, [editorState]);

    const handleAddLink = () => {
        const selection = editorState.getSelection();
        const link = prompt('Please enter the URL of your link');
        if (!link) {
            setEditorState(RichUtils.toggleLink(editorState, selection, null));
            return;
        }
        const content = editorState.getCurrentContent();
        const contentWithEntity = content.createEntity('LINK', 'MUTABLE', {
            url: link,
        });
        const newEditorState = EditorState.push(editorState, contentWithEntity, 'apply-entity');
        const entityKey = contentWithEntity.getLastCreatedEntityKey();
        setEditorState(RichUtils.toggleLink(newEditorState, selection, entityKey));
    };

    const handleKeyCommand = (command: DraftEditorCommand) => {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            setEditorState(newState);
            return 'handled';
        }
        return 'not-handled';
    };

    const handleTogggleClick = (e: React.MouseEvent, inlineStyle: string) => {
        e.preventDefault();
        setEditorState(RichUtils.toggleInlineStyle(editorState, inlineStyle));
    };

    const handleBlockClick = (e: React.MouseEvent, blockType: string) => {
        e.preventDefault();
        setEditorState(RichUtils.toggleBlockType(editorState, blockType));
    };

    return (
        <div className="texteditor">
            <button onMouseDown={(e) => handleBlockClick(e, 'unstyled')}>normal</button>
            <button onMouseDown={(e) => handleTogggleClick(e, 'BOLD')}>bold</button>
            <button onMouseDown={(e) => handleTogggleClick(e, 'UNDERLINE')}>underline</button>
            <button onMouseDown={(e) => handleTogggleClick(e, 'ITALIC')}>italic</button>
            <button onMouseDown={(e) => handleTogggleClick(e, 'STRIKETHROUGH')}>strikthrough</button>
            <button onMouseDown={(e) => handleBlockClick(e, 'ordered-list-item')}>ordered list</button>
            <button onMouseDown={(e) => handleBlockClick(e, 'unordered-list-item')}>unordered list</button>
            <button
                disabled={editorState.getSelection().isCollapsed()}
                onMouseDown={(e) => {
                    e.preventDefault();
                    handleAddLink();
                }}
            >
                link
            </button>
            <Editor
                editorState={editorState}
                onChange={setEditorState}
                handleKeyCommand={handleKeyCommand}
                blockRendererFn={mediaBlockRenderer}
            />
        </div>
    );
};

export default TextEditor;
